import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const NUDGE_KEY = 'ov_feedback_nudge_dismissed';
const NUDGE_DELAY_MS = 20000; // 20 seconds

export default function FeedbackWidget() {
  const [open, setOpen]       = useState(false);
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus]   = useState('idle'); // idle | sending | done | error
  const [errMsg, setErrMsg]   = useState('');
  const [nudge, setNudge]     = useState(false);

  useEffect(() => {
    if (localStorage.getItem(NUDGE_KEY)) return;
    const t = setTimeout(() => setNudge(true), NUDGE_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  function dismissNudge() {
    setNudge(false);
    localStorage.setItem(NUDGE_KEY, '1');
  }

  function openFeedback() {
    dismissNudge();
    setOpen(true);
  }

  function reset() {
    setName(''); setEmail(''); setMessage('');
    setStatus('idle'); setErrMsg('');
  }

  async function submit(e) {
    e.preventDefault();
    if (!email.trim() || !message.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch(`${API}/api/feedback/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), message: message.trim() }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Something went wrong');
      }
      setStatus('done');
    } catch (err) {
      setErrMsg(err.message);
      setStatus('error');
    }
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">

      {/* Panel */}
      {open && (
        <div className="w-80 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-primary-600 dark:bg-primary-700">
            <div>
              <p className="text-white font-semibold text-sm">Share Feedback</p>
              <p className="text-primary-200 text-xs">We read every message</p>
            </div>
            <button
              onClick={() => { setOpen(false); reset(); }}
              className="text-primary-200 hover:text-white transition-colors text-lg leading-none"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="p-4">
            {status === 'done' ? (
              <div className="text-center py-6">
                <div className="mb-2 flex justify-center">
                  <svg className="w-10 h-10 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="font-semibold text-neutral-800 dark:text-neutral-100">Thanks!</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Your feedback has been sent.</p>
                <button
                  onClick={reset}
                  className="mt-4 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                    Name <span className="text-neutral-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full text-sm px-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full text-sm px-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                    Feedback <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="What do you think? Any bugs or feature requests?"
                    required
                    rows={4}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-xs text-red-500">{errMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending' || !email.trim() || !message.trim()}
                  className="w-full py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
                >
                  {status === 'sending' ? 'Sending…' : 'Send Feedback'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Nudge bubble */}
      {nudge && !open && (
        <div className="relative bg-white dark:bg-gray-900 border border-neutral-200 dark:border-neutral-700 rounded-2xl shadow-xl p-4 w-56 text-sm">
          {/* Close button — inside the card */}
          <button
            onClick={dismissNudge}
            className="absolute top-2.5 right-3 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 text-xs leading-none"
            aria-label="Dismiss"
          >✕</button>
          <p className="font-semibold text-neutral-800 dark:text-neutral-100 mb-1 pr-5">Got a minute? 💬</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
            This site is a work in progress — your feedback helps shape what gets built next.
          </p>
          <button
            onClick={openFeedback}
            className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
          >
            Share feedback →
          </button>
          {/* Tail pointing down toward the button */}
          <div className="absolute -bottom-[7px] right-6 w-3 h-3 bg-white dark:bg-gray-900 border-r border-b border-neutral-200 dark:border-neutral-700 rotate-45" />
        </div>
      )}

      {/* Trigger button */}
      <button
        onClick={() => { dismissNudge(); setOpen((o) => !o); }}
        className="w-14 h-14 rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95"
        aria-label="Open feedback"
      >
        {open ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 16V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2h11l4 4v-4h-1a2 2 0 002-2z" />
          </svg>
        )}
      </button>
    </div>
  );
}
